import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const AssignCompaniesModal = ({ agent, onClose, onSuccess }) => {
  const [allCompanies, setAllCompanies] = useState([]);
  const [assignedCompanies, setAssignedCompanies] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [leftSelected, setLeftSelected] = useState([]);
  const [rightSelected, setRightSelected] = useState([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch all companies
      const companiesResponse = await axios.get('/api/companies', {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 10000 }
      });

      // Fetch existing assignments for this agent
      const assignmentsResponse = await axios.get('/api/admin/agent-assignments', {
        headers: { Authorization: `Bearer ${token}` },
        params: { agent_id: agent.id }
      });

      const companies = companiesResponse.data.companies || [];
      const assignments = assignmentsResponse.data.assignments || [];
      
      // Get list of assigned company IDs
      const assignedIds = assignments
        .filter(a => a.is_active)
        .map(a => a.company_id);

      // Split companies into assigned and available
      const assigned = companies.filter(c => assignedIds.includes(c.id));
      const available = companies.filter(c => !assignedIds.includes(c.id));

      setAllCompanies(companies);
      setAssignedCompanies(assigned);
      setAvailableCompanies(available);
    } catch (error) {
      toast.error('שגיאה בטעינת נתונים');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      // Get all company IDs that should be assigned
      const finalAssignedIds = assignedCompanies.map(c => c.id);

      // Fetch current assignments to get assignment IDs for deletion
      const assignmentsResponse = await axios.get('/api/admin/agent-assignments', {
        headers: { Authorization: `Bearer ${token}` },
        params: { agent_id: agent.id }
      });

      const currentAssignments = assignmentsResponse.data.assignments || [];

      // Delete removed assignments
      const availableIds = availableCompanies.map(c => c.id);
      const assignmentsToDelete = currentAssignments.filter(a => 
        availableIds.includes(a.company_id) && a.is_active
      );

      const deletePromises = assignmentsToDelete.map(assignment =>
        axios.delete(`/api/admin/agent-assignments/${assignment.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      // Create new assignments
      const assignedIds = assignedCompanies.map(c => c.id);
      const currentAssignedIds = currentAssignments
        .filter(a => a.is_active)
        .map(a => a.company_id);
      
      const newCompanyIds = assignedIds.filter(id => !currentAssignedIds.includes(id));

      const createPromises = newCompanyIds.map(companyId =>
        axios.post('/api/admin/agent-assignments', {
          agent_id: agent.id,
          company_id: companyId,
          access_level: 'full'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      await Promise.all([...deletePromises, ...createPromises]);

      toast.success('החברות עודכנו בהצלחה');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בשמירת הקצאות');
    } finally {
      setSaving(false);
    }
  };

  // Filter functions
  const filterCompanies = (companies, searchTerm) => {
    if (!searchTerm) return companies;
    const lower = searchTerm.toLowerCase();
    return companies.filter(c =>
      c.name?.toLowerCase().includes(lower) ||
      c.business_number?.toLowerCase().includes(lower)
    );
  };

  const filteredAvailable = filterCompanies(availableCompanies, leftSearch);
  const filteredAssigned = filterCompanies(assignedCompanies, rightSearch);

  // Move functions
  const moveToAssigned = (companies) => {
    setAssignedCompanies(prev => [...prev, ...companies]);
    setAvailableCompanies(prev => prev.filter(c => !companies.includes(c)));
    setLeftSelected([]);
  };

  const moveToAvailable = (companies) => {
    setAvailableCompanies(prev => [...prev, ...companies]);
    setAssignedCompanies(prev => prev.filter(c => !companies.includes(c)));
    setRightSelected([]);
  };

  const moveAllToAssigned = () => {
    const toMove = filterCompanies(availableCompanies, leftSearch);
    moveToAssigned(toMove);
    setLeftSearch('');
  };

  const moveAllToAvailable = () => {
    const toMove = filterCompanies(assignedCompanies, rightSearch);
    moveToAvailable(toMove);
    setRightSearch('');
  };

  const toggleLeftSelection = (company) => {
    setLeftSelected(prev =>
      prev.includes(company)
        ? prev.filter(c => c !== company)
        : [...prev, company]
    );
  };

  const toggleRightSelection = (company) => {
    setRightSelected(prev =>
      prev.includes(company)
        ? prev.filter(c => c !== company)
        : [...prev, company]
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">טוען...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BuildingOfficeIcon className="w-8 h-8 text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">הקצאת חברות</h2>
              <p className="text-indigo-100 text-sm">
                {agent.user?.first_name} {agent.user?.last_name} ({agent.employee_id})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Stats Bar */}
        <div className="bg-gray-50 px-6 py-3 border-b flex items-center justify-between">
          <div className="flex gap-6 text-sm">
            <div>
              <span className="text-gray-600">זמינות: </span>
              <span className="font-bold text-gray-900">{availableCompanies.length}</span>
            </div>
            <div>
              <span className="text-gray-600">מוקצות: </span>
              <span className="font-bold text-indigo-600">{assignedCompanies.length}</span>
            </div>
            <div>
              <span className="text-gray-600">סה"כ: </span>
              <span className="font-bold text-gray-900">{allCompanies.length}</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          <div className="grid grid-cols-[1fr_auto_1fr] gap-4">
            {/* Left Panel - Available Companies */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h3 className="font-semibold text-gray-900 mb-2">חברות זמינות ({filteredAvailable.length})</h3>
                <div className="relative">
                  <MagnifyingGlassIcon className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="חפש חברה..."
                    value={leftSearch}
                    onChange={(e) => setLeftSearch(e.target.value)}
                    className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              <div className="overflow-y-auto bg-white" style={{ height: '400px' }}>
                {filteredAvailable.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <BuildingOfficeIcon className="w-16 h-16 mb-2" />
                    <p className="text-sm">אין חברות זמינות</p>
                  </div>
                ) : (
                  filteredAvailable.map(company => (
                    <div
                      key={company.id}
                      onClick={() => toggleLeftSelection(company)}
                      className={`px-4 py-3 border-b cursor-pointer transition-colors ${
                        leftSelected.includes(company)
                          ? 'bg-indigo-50 border-indigo-200'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          leftSelected.includes(company)
                            ? 'bg-indigo-600 border-indigo-600'
                            : 'border-gray-300'
                        }`}>
                          {leftSelected.includes(company) && (
                            <CheckIcon className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{company.name}</p>
                          <p className="text-xs text-gray-500">{company.business_number}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Center Panel - Controls */}
            <div className="flex flex-col items-center justify-center gap-2">
              <button
                onClick={() => moveToAssigned(leftSelected)}
                disabled={leftSelected.length === 0}
                className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                title="העבר נבחרים ימינה"
              >
                <ChevronLeftIcon className="w-6 h-6" />
              </button>
              <button
                onClick={moveAllToAssigned}
                disabled={filteredAvailable.length === 0}
                className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                title="העבר הכל ימינה"
              >
                <ChevronDoubleLeftIcon className="w-6 h-6" />
              </button>
              
              <div className="h-8"></div>
              
              <button
                onClick={moveAllToAvailable}
                disabled={filteredAssigned.length === 0}
                className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                title="העבר הכל שמאלה"
              >
                <ChevronDoubleRightIcon className="w-6 h-6" />
              </button>
              <button
                onClick={() => moveToAvailable(rightSelected)}
                disabled={rightSelected.length === 0}
                className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                title="העבר נבחרים שמאלה"
              >
                <ChevronRightIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Right Panel - Assigned Companies */}
            <div className="border border-indigo-200 rounded-lg overflow-hidden bg-indigo-50/30">
              <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200">
                <h3 className="font-semibold text-indigo-900 mb-2">חברות מוקצות ({filteredAssigned.length})</h3>
                <div className="relative">
                  <MagnifyingGlassIcon className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-indigo-400" />
                  <input
                    type="text"
                    placeholder="חפש חברה..."
                    value={rightSearch}
                    onChange={(e) => setRightSearch(e.target.value)}
                    className="w-full pr-10 pl-4 py-2 border border-indigo-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  />
                </div>
              </div>
              <div className="overflow-y-auto bg-white" style={{ height: '400px' }}>
                {filteredAssigned.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-indigo-300">
                    <BuildingOfficeIcon className="w-16 h-16 mb-2" />
                    <p className="text-sm">אין חברות מוקצות</p>
                  </div>
                ) : (
                  filteredAssigned.map(company => (
                    <div
                      key={company.id}
                      onClick={() => toggleRightSelection(company)}
                      className={`px-4 py-3 border-b border-indigo-100 cursor-pointer transition-colors ${
                        rightSelected.includes(company)
                          ? 'bg-indigo-100 border-indigo-300'
                          : 'hover:bg-indigo-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          rightSelected.includes(company)
                            ? 'bg-indigo-600 border-indigo-600'
                            : 'border-indigo-300'
                        }`}>
                          {rightSelected.includes(company) && (
                            <CheckIcon className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{company.name}</p>
                          <p className="text-xs text-indigo-600">{company.business_number}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex items-center justify-between">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            ביטול
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>שומר...</span>
              </>
            ) : (
              <span>שמור שינויים</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignCompaniesModal;
